import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/api';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

const defaultTheme = createTheme();

const CreateEvent = () => {
  const [eventName, setEventName] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventType, setEventType] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);

  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.eventTypes.getAll());
        const data = await response.json();
        setEventTypes(data);
      } catch (error) {
        console.error('Etkinlik türleri yüklenirken hata:', error);
      }
    };

    fetchEventTypes();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result); // base64 string
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eventName || !eventDate || !eventType) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setLoading(true);

    try {
      let base64Image = null;
      if (selectedImage) {
        base64Image = await getBase64(selectedImage);
      }

      const payload = {
        name: eventName,
        description,
        eventDate,
        eventType,
        imageBase64: base64Image
      };

      const response = await fetch(API_ENDPOINTS.events.create(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Etkinlik başarıyla oluşturuldu!');
        navigate(`/event/${data.codeHash}`);
      } else {
        if (data.needsGoogleAuth) {
          toast.error('Google Drive yetkilendirmesi gerekiyor');
          navigate('/login');
        } else {
          throw new Error(data.error || 'Bir hata oluştu');
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // AI ile açıklama oluştur fonksiyonu
  const generateEventDescription = async () => {
    try {
      setIsGeneratingContent(true);
      const selectedType = eventTypes.find(type => type.id === eventType);
      const prompt = `${selectedType?.name || 'Etkinlik'} davetimiz için katılımcıların masadaki qr'ı okutup davette fotoğraf videoları yüklemesi için bir açıklama yaz. Sadece bana açıklamayı ver. ${'Etkinlik adımız: ' + eventName + '. Etkinlik adı yapacağın açıklamada en başında yazmasın sonunda Sevgiyle, Etkinlik Adı şeklinde yazabilir.' || 'Etkinlik adı bulunmuyor.'}`;
      const response = await fetch(API_ENDPOINTS.ai.generate(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          question: prompt,
          model: 'meta-ai'
        })
      });
      if (!response.ok) throw new Error('API yanıt vermedi');
      const data = await response.json();
      let desc = data.response;
      const unwantedText = "I am still improving my command of other languages, and I may make errors while attempting them.";
      setDescription(desc.replaceAll(unwantedText, ""));
      toast.success('Açıklama oluşturuldu');
    } catch (error) {
      toast.error('İçerik oluşturulurken bir hata oluştu: ' + error.message);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Yeni Etkinlik Oluştur
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Etkinlik Adı"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              margin="normal"
              required
              error={!eventName}
              helperText={!eventName && "Etkinlik adı zorunludur"}
            />

            <TextField
              fullWidth
              label="Etkinlik Tarihi"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              margin="normal"
              required
              error={!eventDate}
              helperText={!eventDate && "Etkinlik tarihi zorunludur"}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
            />

            <FormControl fullWidth margin="normal" required error={!eventType}>
              <InputLabel>Etkinlik Türü</InputLabel>
              <Select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {!eventType && (
                <Typography variant="caption" color="error">
                  Etkinlik türü zorunludur
                </Typography>
              )}
            </FormControl>

            {/* Açıklama alanı ve AI butonu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Açıklama"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                multiline
                rows={4}
                sx={{ flex: 1 }}
              />
              <Tooltip title="AI ile oluştur" arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={generateEventDescription}
                    disabled={isGeneratingContent || !eventType}
                    sx={{
                      minWidth: 'auto',
                      height: 56, // TextField'ın yüksekliğine yakın
                      width: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0
                    }}
                  >
                    {isGeneratingContent ? (
                      <CircularProgress size={28} />
                    ) : (
                      <AutoFixHigh fontSize="medium" />
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ mt: 2, mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="event-image"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="event-image">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Etkinlik Görseli Yükle
                </Button>
              </label>
              {imagePreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={imagePreview}
                    alt="Önizleme"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              disabled={loading || !eventName || !eventDate || !eventType}
            >
              {loading ? 'Oluşturuluyor...' : 'Etkinlik Oluştur'}
            </Button>
          </form>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default CreateEvent;
